import React, { useRef, useState } from 'react';
import styled from '@emotion/styled';
import { Link } from 'react-router-dom';
import { StaysSyncError, SyncReview, approveSyncReviews } from '../../../../api/stays-sync-client';
import { AppButton } from '../../../button/app-button';
import { ConfirmDialog } from '../../../modal/confirm-dialog';
import { listItemMotion, m } from '../../../../motion';
import { countOf } from '../../../../function-utils/plural';
import { spacing } from '../../../../style-utils/theme';
import toast from '../../../../utils/toast';
import { isPlainNew, providerName, shortRange } from '../../../../utils/stays-sync';
import { SyncReviewCard } from './sync-review-card';

/**
 * THE CALENDAR CHANGES, AS A LIST (plan S5, screens §2): one card per item,
 * staggered by index (no layout animation on an unbounded list), and — when
 * two or more plain new stays wait — "Add all n new stays" above the list
 * (asks first, naming their dates; the server approves each on its own, so
 * one refusal never blocks the others). `limit` makes the listing tab's
 * compact form: the first n, then "See all".
 */

const List = styled.ul`
  display: flex;
  flex-direction: column;
  gap: ${spacing.sm};
  min-width: 0;
  margin: 0;
  padding: 0;
  list-style: none;
`;

const Item = m.li`
  min-width: 0;
`;

const Bar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${spacing.xs};
  min-width: 0;
`;

export interface SyncReviewListProps {
  items: SyncReview[];
  canManage: boolean;
  zone: string;
  now: Date;
  showListing?: boolean;
  /** Compact: show the first `limit` and a "See all" link to `seeAllHref`. */
  limit?: number;
  total?: number;
  seeAllHref?: string;
  onChanged: () => void;
}

export const SyncReviewList: React.FC<SyncReviewListProps> = ({
  items,
  canManage,
  zone,
  now,
  showListing = false,
  limit,
  total,
  seeAllHref,
  onChanged,
}) => {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const guard = useRef(false);

  const shown = limit ? items.slice(0, limit) : items;
  const plain = canManage ? items.filter(isPlainNew) : [];
  const sites = Array.from(new Set(plain.map(r => providerName(r.provider))));
  const from = sites.length === 1 ? sites[0] : 'booking sites';
  const count = total ?? items.length;

  const addAll = async () => {
    setConfirming(false);
    if (guard.current) return;
    guard.current = true;
    setBusy(true);
    try {
      const out = await approveSyncReviews(plain.map(r => ({ id: r.id, version: r.version })));
      const ok = out.results.filter(x => x.ok).length;
      const failed = out.results.length - ok;
      if (failed) toast.error(`${countOf(ok, 'stay')} added · ${countOf(failed, 'stay')} could not be added`);
      else toast.success(`${countOf(ok, 'stay')} added`);
    } catch (err) {
      const x = err as StaysSyncError;
      toast.error(x && x.status > 0 && x.message ? x.message : 'The stays could not be added. Please try again.');
    } finally {
      guard.current = false;
      setBusy(false);
      onChanged();
    }
  };

  return (
    <>
      {plain.length >= 2 ? (
        <Bar>
          <AppButton variant="secondary" type="button" loading={busy} loadingLabel="Adding…" onClick={() => setConfirming(true)}>
            {`Add all ${plain.length} new stays`}
          </AppButton>
        </Bar>
      ) : null}
      <List data-testid="sync-review-list">
        {shown.map((r, i) => (
          <Item key={r.id} {...listItemMotion(i % 50)} layout={false}>
            <SyncReviewCard review={r} canManage={canManage} zone={zone} now={now} showListing={showListing} onChanged={onChanged} />
          </Item>
        ))}
      </List>
      {limit && seeAllHref && count > shown.length ? (
        <div>
          <Link to={seeAllHref}>{`See all ${countOf(count, 'change')}`}</Link>
        </div>
      ) : null}
      <ConfirmDialog
        open={confirming}
        title={`Add ${countOf(plain.length, 'stay')} from ${from}?`}
        message={`These stays are added to the calendar: ${plain.map(r => shortRange(r.proposed.check_in, r.proposed.check_out)).join(', ')}.`}
        primaryLabel={`Add ${countOf(plain.length, 'stay')}`}
        onPrimary={addAll}
        dismissLabel="Not now"
        onDismiss={() => setConfirming(false)}
      />
    </>
  );
};
